import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Clock, Zap, CreditCard, Send, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";

interface BookingFlowProps {
  chargingSpot: {
    id: string;
    title: string;
    price_per_hour: number;
    availability_mode: string;
    [key: string]: any;
  };
}

const BookingFlow = ({ chargingSpot }: BookingFlowProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const mode = chargingSpot.availability_mode ?? "reservable";

  if (mode === "instant") return <InstantBooking chargingSpot={chargingSpot} user={user} />;
  if (mode === "request") return <RequestBooking chargingSpot={chargingSpot} user={user} queryClient={queryClient} />;
  return <ReservableBooking chargingSpot={chargingSpot} user={user} />;
};

/* ─── Instant: "Jetzt laden" ─── */
function InstantBooking({ chargingSpot, user }: { chargingSpot: any; user: any }) {
  const [hours, setHours] = useState(1);
  const totalPrice = Math.round(hours * Number(chargingSpot.price_per_hour) * 100) / 100;

  const startCheckout = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Bitte melden Sie sich an");

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          chargingSpotId: chargingSpot.id,
          amount: totalPrice,
          mode: "instant",
          hours,
          successUrl: `${window.location.origin}/booking-success`,
          cancelUrl: window.location.href,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Zap className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-foreground">Sofort verfügbar</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Dieser Ladepunkt ist sofort verfügbar. Bezahlen Sie direkt und laden Sie los.
      </p>

      <div className="rounded-xl border border-primary/20 bg-primary/5 p-5 space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Ladedauer wählen</label>
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((h) => (
              <button
                key={h}
                onClick={() => setHours(h)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                  hours === h
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-foreground hover:border-primary/40"
                }`}
              >
                {h}h
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            {hours} Std. × {Number(chargingSpot.price_per_hour).toFixed(2)} €/h
          </span>
          <span className="text-xl font-bold text-foreground">{totalPrice.toFixed(2)} €</span>
        </div>

        <Button
          onClick={() => startCheckout.mutate()}
          disabled={startCheckout.isPending || !user}
          className="w-full gap-2"
          size="lg"
        >
          <CreditCard className="h-4 w-4" />
          {startCheckout.isPending ? "Weiterleitung…" : !user ? "Bitte erst anmelden" : "Jetzt laden & bezahlen"}
        </Button>
        <p className="text-xs text-center text-muted-foreground">Sichere Zahlung</p>
      </div>
    </div>
  );
}

/* ─── Request: "Anfrage senden" ─── */
function RequestBooking({ chargingSpot, user, queryClient }: { chargingSpot: any; user: any; queryClient: any }) {
  const [message, setMessage] = useState("");

  const { data: existingRequest } = useQuery({
    queryKey: ["my_request", chargingSpot.id, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("booking_requests")
        .select("*")
        .eq("charging_spot_id", chargingSpot.id)
        .eq("driver_id", user!.id)
        .eq("status", "pending")
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const sendRequest = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Bitte melden Sie sich an");
      const { error } = await supabase.from("booking_requests").insert({
        charging_spot_id: chargingSpot.id,
        driver_id: user.id,
        message: message || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Anfrage gesendet! Der Host wird benachrichtigt.");
      queryClient.invalidateQueries({ queryKey: ["my_request", chargingSpot.id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (existingRequest) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Anfrage gesendet</h3>
        </div>
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <p className="text-sm text-muted-foreground">
            Ihre Anfrage wurde gesendet. Sie erhalten eine Benachrichtigung, sobald der Host antwortet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-foreground">Nur auf Anfrage</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Dieser Ladepunkt ist nur auf Anfrage verfügbar. Senden Sie eine Anfrage an den Host.
      </p>

      <div className="rounded-xl border border-border bg-card p-5 space-y-3">
        <Textarea
          placeholder="Nachricht an den Host (optional)…"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
        />
        <Button
          onClick={() => sendRequest.mutate()}
          disabled={sendRequest.isPending || !user}
          className="w-full gap-2"
          size="lg"
        >
          <Send className="h-4 w-4" />
          {sendRequest.isPending ? "Wird gesendet…" : !user ? "Bitte erst anmelden" : "Anfrage senden"}
        </Button>
      </div>
    </div>
  );
}

/* ─── Reservable: existing slot-based booking ─── */
function ReservableBooking({ chargingSpot, user }: { chargingSpot: any; user: any }) {
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null);

  const { data: slots, isLoading } = useQuery({
    queryKey: ["available_slots", chargingSpot.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("availability_slots")
        .select("*")
        .eq("charging_spot_id", chargingSpot.id)
        .eq("is_booked", false)
        .gte("start_time", new Date().toISOString())
        .order("start_time", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const startCheckout = useMutation({
    mutationFn: async (slotId: string) => {
      if (!user) throw new Error("Bitte melden Sie sich an");
      const slot = slots?.find((s) => s.id === slotId);
      if (!slot) throw new Error("Slot nicht gefunden");
      const hours = (new Date(slot.end_time).getTime() - new Date(slot.start_time).getTime()) / (1000 * 60 * 60);
      const amount = Math.round(hours * Number(chargingSpot.price_per_hour) * 100) / 100;

      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: {
          slotId,
          chargingSpotId: chargingSpot.id,
          amount,
          mode: "reservable",
          successUrl: `${window.location.origin}/booking-success`,
          cancelUrl: window.location.href,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: (err: Error) => {
      if (err.message.includes("already booked")) {
        toast.error("Dieser Slot wurde bereits gebucht. Bitte wählen Sie einen anderen.");
      } else {
        toast.error(err.message);
      }
    },
  });

  const selectedSlot = slots?.find((s) => s.id === selectedSlotId);
  const hours = selectedSlot
    ? (new Date(selectedSlot.end_time).getTime() - new Date(selectedSlot.start_time).getTime()) / (1000 * 60 * 60)
    : 0;
  const totalPrice = Math.round(hours * Number(chargingSpot.price_per_hour) * 100) / 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Clock className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold text-foreground">Zeitfenster reservieren</h3>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Lädt verfügbare Slots…</p>}
      {!isLoading && slots?.length === 0 && (
        <div className="rounded-lg border border-border bg-muted/30 p-6 text-center">
          <p className="text-sm text-muted-foreground">Derzeit keine freien Zeitfenster verfügbar.</p>
        </div>
      )}

      <div className="space-y-2">
        {slots?.map((slot) => (
          <button
            key={slot.id}
            onClick={() => setSelectedSlotId(slot.id)}
            className={`w-full flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
              selectedSlotId === slot.id ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/40"
            }`}
          >
            <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-medium">
                {format(new Date(slot.start_time), "EEEE, dd. MMMM yyyy", { locale: de })}
              </span>
              <br />
              <span className="text-muted-foreground">
                {format(new Date(slot.start_time), "HH:mm")} – {format(new Date(slot.end_time), "HH:mm")} Uhr
              </span>
            </div>
            <div className="text-right text-sm">
              <span className="font-semibold text-foreground">
                {(
                  ((new Date(slot.end_time).getTime() - new Date(slot.start_time).getTime()) / (1000 * 60 * 60)) *
                  Number(chargingSpot.price_per_hour)
                ).toFixed(2)}{" "}
                €
              </span>
            </div>
          </button>
        ))}
      </div>

      {selectedSlotId && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {hours.toFixed(1)} Stunden × {Number(chargingSpot.price_per_hour).toFixed(2)} €/h
            </span>
            <span className="text-lg font-bold text-foreground">{totalPrice.toFixed(2)} €</span>
          </div>
          <Button
            onClick={() => startCheckout.mutate(selectedSlotId)}
            disabled={startCheckout.isPending || !user}
            className="w-full gap-2"
            size="lg"
          >
            <CreditCard className="h-4 w-4" />
             {startCheckout.isPending ? "Weiterleitung…" : !user ? "Bitte erst anmelden" : "Jetzt bezahlen"}
           </Button>
           <p className="text-xs text-center text-muted-foreground">Sichere Zahlung</p>
        </div>
      )}
    </div>
  );
}

export default BookingFlow;
